import React, {useState, useEffect, useCallback} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { AuthService, CustomerService, DailyRoutesTemplateService } from "../../services";
import { Breadcrumb, Modal, Button, Spinner } from "react-bootstrap";
import { PencilSquare, Trash, Plus, Check } from "react-bootstrap-icons";
import RoutesSection from "./RoutesSection";
import { selectAllActiveDrivers, selectAllActiveVehicles } from "./../../store";
import {
  runRouteChecks,
  joinRouteNames,
  formatRouteAddressList,
  getCustomerTypeLabel,
} from "./routeChecks";
import { navigateToPreviousPage } from "../../shared";

const ROUTE_CLEAR_INBOUND_CONFIRM =
  "Are you sure you want to delete all inbound routes from this template?";
const ROUTE_CLEAR_OUTBOUND_CONFIRM =
  "Are you sure you want to delete all outbound routes from this template?";

const emptyCheckRoutesResult = () => ({
  inbound: [],
  outbound: [],
  attendance: [],
  addressMismatch: [],
  customerTypeMismatch: [],
  customerSpecialNeedsMismatch: [],
  customerNoteMismatch: [],
  dischargedOnRoute: [],
});

const ViewDailyTemplate = () => {
  const params = useParams();
  const navigate = useNavigate();
  const drivers = useSelector(selectAllActiveDrivers);
  const vehicles = useSelector(selectAllActiveVehicles);
  const [template, setTemplate] = useState(null);
  const [inboundRoutes, setInboundRoutes] = useState([]);
  const [outboundRoutes, setOutboundRoutes] = useState([]);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [showCheckRoutesModal, setShowCheckRoutesModal] = useState(false);
  const [checkRoutesResult, setCheckRoutesResult] = useState(emptyCheckRoutesResult);
  const [checkingRoutes, setCheckingRoutes] = useState(false);
  const [customerTypeFixing, setCustomerTypeFixing] = useState({});
  const [customerSpecialNeedsFixing, setCustomerSpecialNeedsFixing] = useState({});
  const [customerNoteFixing, setCustomerNoteFixing] = useState({});
  const canEditTemplateRoutes = AuthService.canEditRouteTemplates();

  useEffect(() => {
    fetchTemplate();
  }, [params.id]);

  useEffect(() => {
    CustomerService.getAllCustomers()
      .then((res) => setCustomers(res?.data || []))
      .catch(() => setCustomers([]));
  }, []);

  const fetchTemplate = () => {
    return DailyRoutesTemplateService.getDailyRoutesTemplate(params.id).then((res) => {
      const data = res.data;
      setTemplate(data);
      setNewName(data.name);
      const inbound = data.routes?.filter((route) => route.type === "inbound") || [];
      const outbound = data.routes?.filter((route) => route.type === "outbound") || [];
      setInboundRoutes(inbound);
      setOutboundRoutes(outbound);
      return { data, inbound, outbound };
    });
  };

  const goBack = () => {
    navigateToPreviousPage(navigate, '/trans-routes/daily-templates/list');
  };

  const openEditNameModal = () => {
    if (!canEditTemplateRoutes) return;
    setShowEditNameModal(true);
  };

  const closeEditNameModal = () => {
    setShowEditNameModal(false);
    setNewName(template.name);
  };

  const saveName = () => {
    if (!newName || newName.trim() === '') {
      alert('Please enter a template name');
      return;
    }

    setSaving(true);
    DailyRoutesTemplateService.updateDailyRoutesTemplate(params.id, {
      ...template,
      name: newName
    }).then(() => {
      setSaving(false);
      setShowEditNameModal(false);
      fetchTemplate();
    }).catch(err => {
      setSaving(false);
      alert('Failed to update template name');
    });
  };

  const confirmDelete = () => {
    if (!canEditTemplateRoutes) return;
    setShowDeleteModal(true);
  };

  const deleteTemplate = () => {
    setDeleting(true);
    DailyRoutesTemplateService.deleteDailyRoutesTemplate(params.id).then(() => {
      setDeleting(false);
      setShowDeleteModal(false);
      navigate('/trans-routes/daily-templates/list');
    }).catch(err => {
      setDeleting(false);
      alert('Failed to delete template');
    });
  };

  const goToCreateRoute = (type) => {
    if (!canEditTemplateRoutes) return;
    const qs = type ? `?type=${encodeURIComponent(type)}` : "";
    navigate(`/trans-routes/daily-templates/${params.id}/create-route${qs}`);
  };

  const handleTemplateImportSave = (finalPayload) => {
    const newRoute = { ...finalPayload };
    delete newRoute.id;
    delete newRoute._id;
    return DailyRoutesTemplateService.getDailyRoutesTemplate(params.id).then((res) => {
      const doc = res.data;
      const nextRoutes = [...(doc.routes || []), newRoute];
      return DailyRoutesTemplateService.updateDailyRoutesTemplate(params.id, {
        ...doc,
        routes: nextRoutes,
      });
    }).then(() => fetchTemplate());
  };

  const handleTemplateCopyAllSave = (finalPayloads) => {
    const toAppend = (finalPayloads || []).map((p) => {
      const r = { ...p };
      delete r.id;
      delete r._id;
      return r;
    });
    if (toAppend.length === 0) {
      return Promise.resolve();
    }
    return DailyRoutesTemplateService.getDailyRoutesTemplate(params.id).then((res) => {
      const doc = res.data;
      const nextRoutes = [...(doc.routes || []), ...toAppend];
      return DailyRoutesTemplateService.updateDailyRoutesTemplate(params.id, {
        ...doc,
        routes: nextRoutes,
      });
    }).then(() => fetchTemplate());
  };

  const clearRoutesByType = (routeType) => {
    return DailyRoutesTemplateService.getDailyRoutesTemplate(params.id)
      .then((res) => {
        const doc = res.data;
        const remaining = (doc.routes || []).filter((r) => r.type !== routeType);
        return DailyRoutesTemplateService.updateDailyRoutesTemplate(params.id, {
          ...doc,
          routes: remaining,
        });
      })
      .then(() => fetchTemplate())
      .catch(() => {
        window.alert("Failed to clear routes.");
      });
  };

  const templateAttendanceDate = useCallback((t) => {
    if (!t?.template_date) return new Date();
    const m = moment(t.template_date, ["MM/DD/YYYY", "M/D/YYYY"], true);
    return m.isValid() ? m.toDate() : new Date();
  }, []);

  const applyCheckResult = useCallback(
    async (inboundList, outboundList, templateDoc) => {
      const t = templateDoc || template;
      const result = await runRouteChecks({
        inboundRoutes: inboundList,
        outboundRoutes: outboundList,
        customers,
        attendanceDate: templateAttendanceDate(t),
      });
      setCheckRoutesResult(result);
      setShowCheckRoutesModal(true);
    },
    [customers, template, templateAttendanceDate]
  );

  const runTemplateCheckRoutes = async ({ showSpinner = true } = {}) => {
    if (showSpinner) setCheckingRoutes(true);
    try {
      const inbound = inboundRoutes.map((r) => ({ ...r, id: r._id }));
      const outbound = outboundRoutes.map((r) => ({ ...r, id: r._id }));
      await applyCheckResult(inbound, outbound, template);
    } catch (e) {
      console.error(e);
      window.alert("Failed to check routes.");
    } finally {
      if (showSpinner) setCheckingRoutes(false);
    }
  };

  const updateTemplateCustomerOnRoutes = async (issue, field, value) => {
    const res = await DailyRoutesTemplateService.getDailyRoutesTemplate(params.id);
    const doc = res.data;
    const idSet = new Set((issue.mismatchedRoutes || []).map((r) => String(r.routeId)));
    const nextRoutes = (doc.routes || []).map((route) => {
      const rid = String(route._id || route.id || "");
      if (!idSet.has(rid)) return route;
      return {
        ...route,
        route_customer_list: (route.route_customer_list || []).map((c) =>
          c.customer_id === issue.customerId ? { ...c, [field]: value } : c
        ),
      };
    });
    await DailyRoutesTemplateService.updateDailyRoutesTemplate(params.id, { ...doc, routes: nextRoutes });
    const { data, inbound, outbound } = await fetchTemplate();
    const inboundMapped = inbound.map((r) => ({ ...r, id: r._id }));
    const outboundMapped = outbound.map((r) => ({ ...r, id: r._id }));
    await applyCheckResult(inboundMapped, outboundMapped, data);
  };

  const syncCustomerTypeForMismatchTemplate = async (issue) => {
    if (!issue?.customerId || !issue?.dbType || !issue?.mismatchedRoutes?.length) return;
    setCustomerTypeFixing((prev) => ({ ...prev, [issue.customerId]: true }));
    try {
      await updateTemplateCustomerOnRoutes(issue, "customer_type", issue.dbType);
    } catch (error) {
      console.error(error);
      window.alert(`Failed to sync member type for ${issue.customerName}.`);
    } finally {
      setCustomerTypeFixing((prev) => ({ ...prev, [issue.customerId]: false }));
    }
  };

  const syncCustomerSpecialNeedsForMismatchTemplate = async (issue) => {
    if (!issue?.customerId || !issue?.mismatchedRoutes?.length) return;
    setCustomerSpecialNeedsFixing((prev) => ({ ...prev, [issue.customerId]: true }));
    try {
      await updateTemplateCustomerOnRoutes(issue, "customer_special_needs", issue.dbNoteToDriver || "");
    } catch (error) {
      console.error(error);
      window.alert(`Failed to sync note to driver for ${issue.customerName}.`);
    } finally {
      setCustomerSpecialNeedsFixing((prev) => ({ ...prev, [issue.customerId]: false }));
    }
  };

  const syncCustomerNoteForMismatchTemplate = async (issue) => {
    if (!issue?.customerId || !issue?.mismatchedRoutes?.length) return;
    setCustomerNoteFixing((prev) => ({ ...prev, [issue.customerId]: true }));
    try {
      await updateTemplateCustomerOnRoutes(issue, "customer_note", issue.dbNoteToDriver || "");
    } catch (error) {
      console.error(error);
      window.alert(`Failed to sync route note for ${issue.customerName}.`);
    } finally {
      setCustomerNoteFixing((prev) => ({ ...prev, [issue.customerId]: false }));
    }
  };

  if (!template) {
    return (
      <div className="list row mb-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/trans-routes/dashboard">Transportation</Breadcrumb.Item>
          <Breadcrumb.Item href="/trans-routes/daily-templates/list">
            Daily Route Templates
          </Breadcrumb.Item>
          <Breadcrumb.Item active>
            {template.name}
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
          <h4>
            {template.name}
            {canEditTemplateRoutes && <PencilSquare 
              size={20} 
              className="clickable ms-2" 
              onClick={openEditNameModal}
              title="Edit template name"
            />}
            <button className="btn btn-link btn-sm" onClick={goBack}>Back to List</button>
            {canEditTemplateRoutes && <button className="btn btn-danger btn-sm ms-2" onClick={confirmDelete}>
              <Trash size={14} className="me-1" />Delete Template
            </button>}
          </h4>
          <div className="text-muted">
            <small>Template Date: {template.template_date} | Created by: {template.create_by}</small>
          </div>
        </div>
      </div>

      <div className="app-main-content-list-container">
        <div className="list-func-panel mb-3 d-flex flex-wrap align-items-center gap-2">
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => runTemplateCheckRoutes()}
            disabled={checkingRoutes}
          >
            {checkingRoutes ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Checking...
              </>
            ) : (
              <>
                <Check size={16} className="me-2" aria-hidden />
                Check Routes
              </>
            )}
          </button>
          {canEditTemplateRoutes && (
            <button className="btn btn-outline-secondary" type="button" onClick={() => goToCreateRoute()}>
              <Plus size={16} className="me-2" aria-hidden />
              Add New Route
            </button>
          )}
        </div>
        <div className="list row">
          <div className="col-md-12 mb-4">
            <RoutesSection
              transRoutes={inboundRoutes.map((route) => ({ ...route, id: route._id }))}
              copyList={canEditTemplateRoutes ? outboundRoutes.map((route) => ({ ...route, id: route._id })) : undefined}
              sectionName="Inbound Routes"
              drivers={drivers}
              vehicles={vehicles}
              isTemplate={true}
              templateId={params.id}
              canAddNew={false}
              addText="Add New Route"
              copyText={canEditTemplateRoutes ? "Import Outbound Routes" : undefined}
              redirect={goToCreateRoute}
              routeType="inbound"
              showRouteHeaderButtons={canEditTemplateRoutes}
              clearAllRoutesAction={canEditTemplateRoutes ? () => clearRoutesByType("inbound") : null}
              clearConfirmMessage={ROUTE_CLEAR_INBOUND_CONFIRM}
              templateImportSave={canEditTemplateRoutes ? handleTemplateImportSave : null}
              templateCopyAllSave={canEditTemplateRoutes ? handleTemplateCopyAllSave : null}
            />
          </div>
          <div className="col-md-12 mb-4">
            <RoutesSection
              transRoutes={outboundRoutes.map((route) => ({ ...route, id: route._id }))}
              copyList={canEditTemplateRoutes ? inboundRoutes.map((route) => ({ ...route, id: route._id })) : undefined}
              sectionName="Outbound Routes"
              drivers={drivers}
              vehicles={vehicles}
              isTemplate={true}
              templateId={params.id}
              canAddNew={false}
              addText="Add New Route"
              copyText={canEditTemplateRoutes ? "Import Inbound Routes" : undefined}
              redirect={goToCreateRoute}
              routeType="outbound"
              showRouteHeaderButtons={canEditTemplateRoutes}
              clearAllRoutesAction={canEditTemplateRoutes ? () => clearRoutesByType("outbound") : null}
              clearConfirmMessage={ROUTE_CLEAR_OUTBOUND_CONFIRM}
              templateImportSave={canEditTemplateRoutes ? handleTemplateImportSave : null}
              templateCopyAllSave={canEditTemplateRoutes ? handleTemplateCopyAllSave : null}
            />
          </div>
        </div>
      </div>

      <Modal show={showEditNameModal} onHide={closeEditNameModal}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Template Name</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Template Name</label>
            <input 
              type="text" 
              className="form-control" 
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={saving}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditNameModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveName} disabled={saving}>
            {saving ? <><Spinner size="sm" className="me-2" />Saving...</> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the template "{template?.name}"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteTemplate} disabled={deleting}>
            {deleting ? <><Spinner size="sm" className="me-2" />Deleting...</> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCheckRoutesModal} onHide={() => setShowCheckRoutesModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Check Routes Result</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {checkRoutesResult.inbound.length === 0 &&
          checkRoutesResult.outbound.length === 0 &&
          checkRoutesResult.attendance.length === 0 &&
          checkRoutesResult.addressMismatch.length === 0 &&
          checkRoutesResult.customerTypeMismatch.length === 0 &&
          checkRoutesResult.customerSpecialNeedsMismatch.length === 0 &&
          checkRoutesResult.customerNoteMismatch.length === 0 &&
          checkRoutesResult.dischargedOnRoute.length === 0 ? (
            <div className="text-success d-flex align-items-center">
              <Check size={18} className="me-2" />
              <span>No issue found.</span>
            </div>
          ) : (
            <>
              {checkRoutesResult.inbound.length > 0 && (
                <div className="mb-3">
                  <div className="fw-bold mb-2">Inbound Issues</div>
                  {checkRoutesResult.inbound.map((issue, idx) => (
                    <div key={`inbound-issue-${idx}`} className="mb-2">
                      {issue.customerName} appears on multiple routes: {formatRouteAddressList(issue.routes)}
                    </div>
                  ))}
                </div>
              )}
              {checkRoutesResult.outbound.length > 0 && (
                <div>
                  <div className="fw-bold mb-2">Outbound Issues</div>
                  {checkRoutesResult.outbound.map((issue, idx) => (
                    <div key={`outbound-issue-${idx}`} className="mb-2">
                      {issue.customerName} appears on multiple routes: {formatRouteAddressList(issue.routes)}
                    </div>
                  ))}
                </div>
              )}
              {checkRoutesResult.attendance.length > 0 && (
                <div className="mt-3">
                  <div className="fw-bold mb-2">Attendance Note Issues</div>
                  {checkRoutesResult.attendance.map((issue, idx) => (
                    <div key={`attendance-issue-${idx}`} className="mb-2">
                      {issue.customerName} is scheduled absent on {issue.noteDate} but still appears on Route{" "}
                      {joinRouteNames(issue.routeNames)}.
                    </div>
                  ))}
                </div>
              )}
              {checkRoutesResult.addressMismatch.length > 0 && (
                <div className="mt-3">
                  <div className="fw-bold mb-2">Address Mismatch Issues</div>
                  {checkRoutesResult.addressMismatch.map((issue, idx) => (
                    <div key={`address-mismatch-issue-${idx}`} className="mb-2">
                      Customer {issue.customerName}&apos;s address: {issue.routeAddress || "N/A"} is not matching his
                      existing addresses stored in system, on route {joinRouteNames(issue.routeNames)}.
                    </div>
                  ))}
                </div>
              )}
              {checkRoutesResult.customerTypeMismatch.length > 0 && (
                <div className="mt-3">
                  <div className="fw-bold mb-2">Member Type Mismatch Issues</div>
                  {checkRoutesResult.customerTypeMismatch.map((issue, idx) => (
                    <div
                      key={`customer-type-mismatch-issue-${idx}`}
                      className="mb-2 d-flex align-items-center justify-content-between"
                      style={{ gap: "12px" }}
                    >
                      <div>
                        Customer {issue.customerName}&apos;s member type is mismatched on route{" "}
                        {joinRouteNames(issue.mismatchedRoutes.map((route) => route.routeName))}. Latest type in system is{" "}
                        {getCustomerTypeLabel(issue.dbType)}.
                      </div>
                      {canEditTemplateRoutes && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={!!customerTypeFixing[issue.customerId]}
                          onClick={() => syncCustomerTypeForMismatchTemplate(issue)}
                        >
                          {customerTypeFixing[issue.customerId] ? "Fixing..." : "Fix"}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {checkRoutesResult.customerSpecialNeedsMismatch.length > 0 && (
                <div className="mt-3">
                  <div className="fw-bold mb-2">Note to Driver Mismatch Issues</div>
                  {checkRoutesResult.customerSpecialNeedsMismatch.map((issue, idx) => (
                    <div
                      key={`customer-special-needs-mismatch-issue-${idx}`}
                      className="mb-2"
                      style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
                    >
                      <div>
                        Customer {issue.customerName}&apos;s note to driver is mismatched on route{" "}
                        {joinRouteNames(issue.mismatchedRoutes.map((route) => route.routeName))}.
                      </div>
                      <div className="mt-1">
                        Latest note in system:
                        <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                          {issue.dbNoteToDriver || "empty"}
                        </div>
                      </div>
                      {canEditTemplateRoutes && (
                        <div className="mt-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={!!customerSpecialNeedsFixing[issue.customerId]}
                            onClick={() => syncCustomerSpecialNeedsForMismatchTemplate(issue)}
                          >
                            {customerSpecialNeedsFixing[issue.customerId] ? "Fixing..." : "Fix"}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {checkRoutesResult.customerNoteMismatch.length > 0 && (
                <div className="mt-3">
                  <div className="fw-bold mb-2">Route Note Mismatch Issues</div>
                  {checkRoutesResult.customerNoteMismatch.map((issue, idx) => (
                    <div
                      key={`customer-note-mismatch-issue-${idx}`}
                      className="mb-2"
                      style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
                    >
                      <div>
                        Customer {issue.customerName}&apos;s route note is mismatched on route{" "}
                        {joinRouteNames(issue.mismatchedRoutes.map((route) => route.routeName))}.
                      </div>
                      <div className="mt-1">
                        Latest note in system:
                        <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                          {issue.dbNoteToDriver || "empty"}
                        </div>
                      </div>
                      {canEditTemplateRoutes && (
                        <div className="mt-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={!!customerNoteFixing[issue.customerId]}
                            onClick={() => syncCustomerNoteForMismatchTemplate(issue)}
                          >
                            {customerNoteFixing[issue.customerId] ? "Fixing..." : "Fix"}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {checkRoutesResult.dischargedOnRoute.length > 0 && (
                <div className="mt-3">
                  <div className="fw-bold mb-2">Discharged Customer Issues</div>
                  {checkRoutesResult.dischargedOnRoute.map((issue, idx) => (
                    <div key={`discharged-route-issue-${idx}`} className="mb-2">
                      Customer {issue.customerName} has been discharged and he is still on route{" "}
                      {joinRouteNames(issue.routeNames)}.
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowCheckRoutesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ViewDailyTemplate;

